import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HelpTooltip from './HelpTooltip';
import {
    getSystemTypeLabel,
    getPressureValveLabel,
    getTraitLabel,
    getSystemTypeHelp,
    getPressureValveHelp,
    getTraitHelp
} from '../config/labelConfig';

const CountryFilter = ({
    countries = [],
    onFilterChange,
    onMapModeChange,
    currentMapMode
}) => {
    const [activeFilters, setActiveFilters] = useState({
        region: 'All',
        systemType: 'All',
        pressureValve: 'All',
        traits: []
    });

    const [showAllTraits, setShowAllTraits] = useState(false);

    // Derive Unique Options
    const regions = ['All', ...new Set(countries.map(c => c.meta?.region).filter(Boolean))].sort();
    const systemTypes = ['All', ...new Set(countries.map(c => c.classifiers?.system_type).filter(Boolean))];
    const pressureValves = ['All', ...new Set(countries.map(c => c.classifiers?.pressure_valve_primary).filter(Boolean))];
    const allTraits = [...new Set(countries.flatMap(c => c.classifiers?.traits || []))].sort();

    // Handle Filter Logic
    useEffect(() => {
        const filtered = countries.filter(c => {
            if (!c.meta || !c.classifiers) return false;

            const matchRegion = activeFilters.region === 'All' || c.meta.region === activeFilters.region;
            const matchSystem = activeFilters.systemType === 'All' || c.classifiers.system_type === activeFilters.systemType;
            const matchValve = activeFilters.pressureValve === 'All' || c.classifiers.pressure_valve_primary === activeFilters.pressureValve;

            // Traits: Must have ALL selected traits (AND logic)
            const matchTraits = activeFilters.traits.length === 0 || activeFilters.traits.every(t => c.classifiers.traits.includes(t));

            return matchRegion && matchSystem && matchValve && matchTraits;
        });

        onFilterChange(filtered);
    }, [activeFilters, countries]);

    const toggleTrait = (trait) => {
        setActiveFilters(prev => ({
            ...prev,
            traits: prev.traits.includes(trait)
                ? prev.traits.filter(t => t !== trait)
                : [...prev.traits, trait]
        }));
    };

    const clearAllFilters = () => {
        setActiveFilters({
            region: 'All',
            systemType: 'All',
            pressureValve: 'All',
            traits: []
        });
    };

    const hasActiveFilters = activeFilters.region !== 'All' ||
        activeFilters.systemType !== 'All' ||
        activeFilters.pressureValve !== 'All' ||
        activeFilters.traits.length > 0;

    // Show first 8 traits by default, rest when expanded
    const visibleTraits = showAllTraits ? allTraits : allTraits.slice(0, 8);

    return (
        <div style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '2rem',
            marginBottom: '2rem',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: '#f8fafc',
                        background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        System Explorer
                    </h3>
                    <p style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.875rem',
                        color: '#94a3b8',
                        fontWeight: 500
                    }}>
                        {countries.length} healthcare systems worldwide
                    </p>
                </div>

                {/* Visual Mode Toggle */}
                <div style={{
                    display: 'flex',
                    background: 'rgba(15, 23, 42, 0.6)',
                    padding: '4px',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                    {[
                        { id: 'default', label: 'Standard Map', icon: '🗺️' },
                        { id: 'pressure_valve', label: 'Pressure Valves', icon: '⚡' }
                    ].map(mode => (
                        <button
                            key={mode.id}
                            onClick={() => onMapModeChange(mode.id)}
                            style={{
                                background: currentMapMode === mode.id
                                    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                                    : 'transparent',
                                color: currentMapMode === mode.id ? '#fff' : '#94a3b8',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                fontWeight: 600,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                boxShadow: currentMapMode === mode.id ? '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none'
                            }}
                        >
                            <span>{mode.icon}</span>
                            <span>{mode.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Filters Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Region Filter */}
                <div>
                    <label style={{
                        display: 'block',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        marginBottom: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700
                    }}>
                        🌍 Region
                    </label>
                    <select
                        value={activeFilters.region}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, region: e.target.value }))}
                        style={{
                            width: '100%',
                            padding: '0.875rem 1rem',
                            borderRadius: '12px',
                            background: 'rgba(15, 23, 42, 0.8)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#f8fafc',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)'}
                    >
                        {regions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                {/* System Type Filter */}
                <div>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        marginBottom: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700
                    }}>
                        🏥 System Archetype
                        <HelpTooltip text="The fundamental organizational model of the healthcare system" position="bottom" />
                    </label>
                    <select
                        value={activeFilters.systemType}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, systemType: e.target.value }))}
                        style={{
                            width: '100%',
                            padding: '0.875rem 1rem',
                            borderRadius: '12px',
                            background: 'rgba(15, 23, 42, 0.8)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#f8fafc',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)'}
                    >
                        <option value="All">All Systems</option>
                        {systemTypes.filter(t => t !== 'All').map(t => (
                            <option key={t} value={t}>{getSystemTypeLabel(t)}</option>
                        ))}
                    </select>
                </div>

                {/* Pressure Valve Filter */}
                <div>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        marginBottom: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700
                    }}>
                        ⚡ Pressure Valve
                        <HelpTooltip text="The primary mechanism used to control demand and costs in the system" position="bottom" />
                    </label>
                    <select
                        value={activeFilters.pressureValve}
                        onChange={(e) => setActiveFilters(prev => ({ ...prev, pressureValve: e.target.value }))}
                        style={{
                            width: '100%',
                            padding: '0.875rem 1rem',
                            borderRadius: '12px',
                            background: 'rgba(15, 23, 42, 0.8)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#f8fafc',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)'}
                    >
                        <option value="All">All Mechanisms</option>
                        {pressureValves.filter(p => p !== 'All').map(p => (
                            <option key={p} value={p}>{getPressureValveLabel(p)}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* DNA Traits Section */}
            <div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700
                    }}>
                        🧬 DNA Traits
                        <HelpTooltip text="Key structural features and policy mechanisms that define how the system operates" position="bottom" />
                    </label>
                    {activeFilters.traits.length > 0 && (
                        <button
                            onClick={() => setActiveFilters(prev => ({ ...prev, traits: [] }))}
                            style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#f87171',
                                padding: '4px 12px',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                fontWeight: 600,
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                e.target.style.borderColor = '#ef4444';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                                e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                            }}
                        >
                            Clear ({activeFilters.traits.length})
                        </button>
                    )}
                </div>

                {/* Traits Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '0.75rem',
                    marginBottom: '1rem'
                }}>
                    {visibleTraits.map(trait => {
                        const isActive = activeFilters.traits.includes(trait);
                        return (
                            <motion.button
                                key={trait}
                                onClick={() => toggleTrait(trait)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                style={{
                                    background: isActive
                                        ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%)'
                                        : 'rgba(15, 23, 42, 0.6)',
                                    border: `1px solid ${isActive ? '#3b82f6' : 'rgba(148, 163, 184, 0.2)'}`,
                                    color: isActive ? '#60a5fa' : '#cbd5e1',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '12px',
                                    fontSize: '0.8125rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '0.5rem',
                                    fontWeight: isActive ? 600 : 500,
                                    boxShadow: isActive ? '0 4px 12px rgba(59, 130, 246, 0.2)' : 'none',
                                    textAlign: 'left'
                                }}
                            >
                                <span style={{ flex: 1 }}>{getTraitLabel(trait)}</span>
                                <HelpTooltip text={getTraitHelp(trait)} position="left" />
                            </motion.button>
                        );
                    })}
                </div>

                {/* Show More/Less Button */}
                {allTraits.length > 8 && (
                    <button
                        onClick={() => setShowAllTraits(!showAllTraits)}
                        style={{
                            width: '100%',
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#60a5fa',
                            padding: '0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            fontWeight: 600,
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                            e.target.style.borderColor = '#3b82f6';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(59, 130, 246, 0.1)';
                            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                        }}
                    >
                        {showAllTraits ? '▲ Show Less' : `▼ Show ${allTraits.length - 8} More Traits`}
                    </button>
                )}
            </div>

            {/* Clear All Filters Button */}
            {hasActiveFilters && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ marginTop: '1.5rem' }}
                >
                    <button
                        onClick={clearAllFilters}
                        style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            color: '#f87171',
                            padding: '1rem',
                            borderRadius: '12px',
                            fontSize: '0.9375rem',
                            cursor: 'pointer',
                            fontWeight: 700,
                            transition: 'all 0.2s',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(220, 38, 38, 0.25) 100%)';
                            e.target.style.borderColor = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)';
                            e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                        }}
                    >
                        🔄 Clear All Filters
                    </button>
                </motion.div>
            )}
        </div>
    );
};

export default CountryFilter;
